import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_STAFF_SCHEDULES, 
  GET_ALL_STAFF
} from '../graphql/queries';
import {
  CREATE_WEEKLY_SCHEDULE,
  UPDATE_STAFF_SCHEDULE,
  DELETE_STAFF_SCHEDULE
} from '../graphql/mutations';
import { Staff, StaffSchedule, ScheduleTimeType } from '../types';

interface UseScheduleProps {
  storeId: string;
}

interface ScheduleHookReturn {
  // Data
  staff: Staff[];
  schedules: StaffSchedule[];
  loading: boolean;
  error: any;
  
  // Week management
  currentWeek: number;
  weekDates: Date[];
  setCurrentWeek: (week: number) => void;
  getWeekStart: (date: Date, weekOffset?: number) => Date;
  
  // Schedule operations
  createWeeklySchedule: (staffId: string, times: ScheduleTimeType[], recurring?: number) => Promise<any>;
  createRecurringSchedule: (staffId: string, times: ScheduleTimeType[], weeks: number) => Promise<any>;
  updateSchedule: (scheduleId: string, data: Partial<StaffSchedule>) => Promise<any>;
  deleteSchedule: (scheduleId: string) => Promise<any>;
  
  // Utility functions
  getStaffScheduleForWeek: (staff: Staff) => { [key: string]: StaffSchedule };
  calculateWorkHours: (startTime: string, endTime: string, breakStart?: string, breakEnd?: string) => number;
  formatTime: (time: string) => string;
  generateTimeSlots: (interval?: number) => string[];
  
  // Refetch functions
  refetchData: () => void;
}

export const useSchedule = ({ storeId }: UseScheduleProps): ScheduleHookReturn => {
  const [currentWeek, setCurrentWeek] = useState(0);

  // GraphQL queries
  const { 
    data: staffData, 
    loading: staffLoading, 
    error: staffError, 
    refetch: refetchStaff 
  } = useQuery(GET_ALL_STAFF, {
    variables: { storeId },
    skip: !storeId,
  });

  const getWeekStart = useCallback((date: Date, weekOffset: number = 0) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) + (weekOffset * 7);
    return new Date(d.setDate(diff));
  }, []);

  const weekDates = useMemo(() => {
    const weekStart = getWeekStart(new Date(), currentWeek);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      days.push(date);
    }
    return days;
  }, [currentWeek, getWeekStart]);

  const { 
    data: scheduleData, 
    loading: scheduleLoading, 
    error: scheduleError, 
    refetch: refetchSchedules 
  } = useQuery(GET_STAFF_SCHEDULES, {
    variables: { 
      storeId,
      startDate: weekDates[0]?.toISOString().split('T')[0],
      endDate: weekDates[6]?.toISOString().split('T')[0]
    },
    skip: !storeId || weekDates.length === 0,
  });

  // GraphQL mutations
  const [createWeeklyScheduleMutation] = useMutation(CREATE_WEEKLY_SCHEDULE);
  const [updateStaffScheduleMutation] = useMutation(UPDATE_STAFF_SCHEDULE);
  const [deleteStaffScheduleMutation] = useMutation(DELETE_STAFF_SCHEDULE);

  // Schedule operations
  const createWeeklySchedule = useCallback(async (staffId: string, times: ScheduleTimeType[], recurring?: number) => {
    const weekStart = getWeekStart(new Date(), currentWeek);
    const result = await createWeeklyScheduleMutation({
      variables: {
        staffId,
        storeId,
        scheduledDate: weekStart.toISOString().split('T')[0],
        times,
        recurring: recurring || 1,
      },
    });
    
    if (result.data?.createWeeklySchedule?.ok) {
      refetchSchedules();
    }
    
    return result;
  }, [createWeeklyScheduleMutation, storeId, currentWeek, getWeekStart, refetchSchedules]);

  const createRecurringSchedule = useCallback(async (staffId: string, times: ScheduleTimeType[], weeks: number) => {
    const weekStart = getWeekStart(new Date(), currentWeek);
    const result = await createWeeklyScheduleMutation({
      variables: {
        staffId,
        storeId,
        scheduledDate: weekStart.toISOString().split('T')[0],
        times,
        recurring: weeks,
      },
    });
    
    if (result.data?.createWeeklySchedule?.ok) {
      refetchSchedules();
    }
    
    return result;
  }, [createWeeklyScheduleMutation, storeId, currentWeek, getWeekStart, refetchSchedules]);

  const updateSchedule = useCallback(async (scheduleId: string, data: Partial<StaffSchedule>) => {
    const result = await updateStaffScheduleMutation({
      variables: {
        staffScheduleId: scheduleId,
        ...data,
      },
    });
    
    if (result.data?.updateStaffSchedule?.ok) {
      refetchSchedules();
    }
    
    return result;
  }, [updateStaffScheduleMutation, refetchSchedules]);

  const deleteSchedule = useCallback(async (scheduleId: string) => {
    const result = await deleteStaffScheduleMutation({
      variables: { staffScheduleId: scheduleId },
    });
    
    if (result.data?.deleteStaffSchedule?.ok) {
      refetchSchedules();
    }
    
    return result;
  }, [deleteStaffScheduleMutation, refetchSchedules]);

  // Utility functions
  const getStaffScheduleForWeek = useCallback((staff: Staff) => {
    const schedules: { [key: string]: StaffSchedule } = {};
    
    if (scheduleData?.allStaff) {
      const staffWithSchedules = scheduleData.allStaff.find((s: Staff) => s.id === staff.id);
      if (staffWithSchedules?.schedules) {
        staffWithSchedules.schedules.forEach((schedule: StaffSchedule) => {
          const scheduleDate = new Date(schedule.schedule.date);
          const dayKey = scheduleDate.toISOString().split('T')[0];
          schedules[dayKey] = schedule;
        });
      }
    }
    
    return schedules;
  }, [scheduleData]);

  const calculateWorkHours = useCallback((startTime: string, endTime: string, breakStart?: string, breakEnd?: string) => {
    if (!startTime || !endTime) return 0;
    
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    let workMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    
    if (breakStart && breakEnd) {
      const breakStartTime = new Date(`2000-01-01T${breakStart}`);
      const breakEndTime = new Date(`2000-01-01T${breakEnd}`);
      const breakMinutes = (breakEndTime.getTime() - breakStartTime.getTime()) / (1000 * 60);
      workMinutes -= breakMinutes;
    }
    
    return Math.max(0, workMinutes / 60);
  }, []);

  const formatTime = useCallback((time: string) => {
    if (!time) return '';
    return time.slice(0, 5);
  }, []);

  const generateTimeSlots = useCallback((interval: number = 30) => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  }, []);

  const refetchData = useCallback(() => {
    refetchStaff();
    refetchSchedules();
  }, [refetchStaff, refetchSchedules]);

  return {
    // Data
    staff: staffData?.allStaff || [],
    schedules: scheduleData?.allStaff?.flatMap((staff: Staff) => staff.schedules || []) || [],
    loading: staffLoading || scheduleLoading,
    error: staffError || scheduleError,
    
    // Week management
    currentWeek,
    weekDates,
    setCurrentWeek,
    getWeekStart,
    
    // Schedule operations
    createWeeklySchedule,
    createRecurringSchedule,
    updateSchedule,
    deleteSchedule,
    
    // Utility functions
    getStaffScheduleForWeek,
    calculateWorkHours,
    formatTime,
    generateTimeSlots,
    
    // Refetch functions
    refetchData,
  };
};
